import React, { useState, useEffect, Fragment } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Home,
  Layout,
  Settings,
  Sparkles,
  Calendar,
  Users,
  BarChart3,
  FileCode,
  Github,
  Zap,
  Briefcase,
  CreditCard,
  Phone,
  Shield,
  FileText,
  Code,
  Layers3,
  Eye,
  Upload,
  Palette,
  ChevronRight,
  ChevronDown,
  Activity,
  Bell,
  TrendingUp,
  LogIn,
  UserPlus,
  Search,
  MapPin,
  ShoppingCart,
  Check,
  History,
  Star,
  DollarSign,
  Mail,
  Building,
  MousePointer,
  Globe,
  BookOpen,
  Megaphone,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { useAppSelector } from '@/store/hooks';
import { EventRole } from '@/store/features/auth/authTypes';
import { persistStore, persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage"; // localStorage

type NavItem = {
  title: string;
  url?: string;
  icon: React.ComponentType<any>;
  badge?: string;
  children?: NavItem[];
  visibilityBadge?: string;
  allowedRoles?: EventRole[];
};

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  return isMobile;
}

// Enhanced navigation data with role-based access control
const navigationSections: { label: string; items: NavItem[] }[] = [
  {
    label: "Main",
    items: [
      {
        title: "Generator",
        url: "/",
        icon: Sparkles,
        allowedRoles: [EventRole.ORGANIZER, EventRole.ADMIN, EventRole.PROMOTER]
      },
      {
        title: "Generate Event",
        url: "/generate",
        icon: Calendar,
        allowedRoles: [EventRole.ORGANIZER, EventRole.ADMIN]
      },
      {
        title: "Dashboard",
        url: "/dashboard",
        icon: Layout,
        allowedRoles: [EventRole.ATTENDEE, EventRole.ORGANIZER, EventRole.PROMOTER, EventRole.ADMIN]
      },
      {
        title: "Discover",
        url: "/discover",
        icon: Search,
        allowedRoles: [EventRole.ATTENDEE, EventRole.ORGANIZER, EventRole.PROMOTER, EventRole.ADMIN]
      },
    ],
  },
  {
    label: "Dashboards",
    items: [
      {
        title: "Promoter",
        url: "/promoter-dashboard",
        icon: Megaphone,
        allowedRoles: [EventRole.PROMOTER, EventRole.ADMIN]
      },
      {
        title: "Attendee",
        url: "/attendee-dashboard",
        icon: Users,
        allowedRoles: [EventRole.ATTENDEE, EventRole.ADMIN]
      },
      {
        title: "Publishing",
        url: "/publishing",
        icon: Globe,
        allowedRoles: [EventRole.ORGANIZER, EventRole.ADMIN]
      },
    ],
  },
  {
    label: "Events & Management",
    items: [
      {
        title: "Events & Templates",
        icon: Calendar,
        allowedRoles: [EventRole.ORGANIZER, EventRole.ADMIN],
        children: [
          { title: "Events", url: "/events", icon: Calendar },
          { title: "Management", url: "/event-management", icon: Settings },
          { title: "Templates", url: "/templates", icon: BookOpen },
          { title: "Template Studio", url: "/template-studio", icon: Layers3 },
          { title: "AI Assistant", url: "/ai-assistant", icon: Zap },
        ],
      },
    ],
  },
  {
    label: "Developer Tools",
    items: [
      {
        title: "Code Editor",
        url: "/code-editor",
        icon: Code,
        badge: "Beta",
        allowedRoles: [EventRole.ADMIN]
      },
      {
        title: "Code Viewer",
        url: "/code-viewer",
        icon: Eye,
        allowedRoles: [EventRole.ADMIN]
      },
      {
        title: "Drag & Drop Builder",
        url: "/drag-drop-builder",
        icon: MousePointer,
        badge: "New",
        allowedRoles: [EventRole.ORGANIZER, EventRole.ADMIN]
      },
      {
        title: "Analytics",
        url: "/analytics",
        icon: BarChart3,
        allowedRoles: [EventRole.ORGANIZER, EventRole.PROMOTER, EventRole.ADMIN]
      },
      {
        title: "Export",
        url: "/export",
        icon: FileCode,
        allowedRoles: [EventRole.ADMIN]
      },
      {
        title: "GitHub",
        url: "/github",
        icon: Github,
        allowedRoles: [EventRole.ADMIN]
      },
      {
        title: "Deploy",
        url: "/deploy",
        icon: Upload,
        allowedRoles: [EventRole.ADMIN]
      },
    ],
  },
  {
    label: "Business",
    items: [
      {
        title: "Customers",
        url: "/customers",
        icon: Users,
        allowedRoles: [EventRole.ORGANIZER, EventRole.ADMIN]
      },
      {
        title: "Email Marketing",
        url: "/email-marketing",
        icon: Mail,
        allowedRoles: [EventRole.ORGANIZER, EventRole.PROMOTER, EventRole.ADMIN]
      },
      {
        title: "Integrations",
        url: "/integrations",
        icon: Phone,
        allowedRoles: [EventRole.ADMIN]
      },
      {
        title: "User Management",
        url: "/user-management",
        icon: Shield,
        allowedRoles: [EventRole.ADMIN]
      },
    ],
  },
  {
    label: "Booking & Commerce",
    items: [
      {
        title: "Booking History",
        url: "/bookings",
        icon: History,
        allowedRoles: [EventRole.ATTENDEE, EventRole.ORGANIZER, EventRole.ADMIN]
      },
      {
        title: "Portfolio",
        url: "/portfolio",
        icon: Star,
        allowedRoles: [EventRole.ORGANIZER, EventRole.PROMOTER, EventRole.ADMIN]
      },
      {
        title: "Pricing",
        url: "/pricing",
        icon: DollarSign,
        allowedRoles: [EventRole.ATTENDEE, EventRole.ORGANIZER, EventRole.PROMOTER, EventRole.ADMIN]
      },
      {
        title: "Contact",
        url: "/contact",
        icon: Phone,
        allowedRoles: [EventRole.ATTENDEE, EventRole.ORGANIZER, EventRole.PROMOTER, EventRole.ADMIN]
      },
    ],
  },
  {
    label: "Authentication",
    items: [
      {
        title: "Sign In",
        url: "/signin",
        icon: LogIn,
        allowedRoles: [] // Show only when NOT authenticated
      },
      {
        title: "Sign Up",
        url: "/signup",
        icon: UserPlus,
        allowedRoles: [] // Show only when NOT authenticated
      },
    ],
  },
];

const bottomLinks: NavItem[] = [
  {
    title: "Settings",
    url: "/settings",
    icon: Settings,
    allowedRoles: [EventRole.ATTENDEE, EventRole.ORGANIZER, EventRole.PROMOTER, EventRole.ADMIN]
  },
];

function SidebarSubMenu({
  item,
  collapsed,
  currentPath,
}: {
  item: NavItem;
  collapsed: boolean;
  currentPath: string;
}) {
  const [open, setOpen] = useState(false);

  // Automatically open if current path matches submenu child
  useEffect(() => {
    if (item.children?.some((child) => currentPath.startsWith(child.url || ""))) {
      setOpen(true);
    }
  }, [currentPath, item.children]);

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        onClick={() => setOpen(!open)}
        className="flex w-full justify-between items-center px-3 py-2.5 hover:bg-sidebar-accent rounded-lg transition-colors duration-200"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <item.icon className={`flex-shrink-0 ${collapsed ? "w-5 h-5" : "w-4 h-4"}`} />
          {!collapsed && (
            <>
              <span className="truncate font-medium dark:text-white light:text-gray-900">{item.title}</span>
              {item.badge && (
                <Badge variant="outline" className="ml-2 text-xs px-2 py-0.5 bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-200">
                  {item.badge}
                </Badge>
              )}
            </>
          )}
        </div>
        {!collapsed && (
          <ChevronDown
            className={`transition-transform duration-200 ${open ? "rotate-180" : "rotate-0"
              } w-4 h-4 text-muted-foreground flex-shrink-0 ml-2`}
          />
        )}
      </SidebarMenuButton>

      {open && !collapsed && item.children && (
        <div className="ml-7 mt-2 space-y-1 border-l border-sidebar-border pl-4">
          {item.children.map((child) => (
            <SidebarMenuItem key={child.title}>
              <NavLink
                to={child.url || "#"}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200 ${isActive
                    ? "bg-gradient-to-r from-theme-primary to-theme-secondary text-white shadow-md"
                    : "text-sidebar-foreground hover:text-foreground hover:bg-sidebar-accent/80"
                  }`
                }
                end
              >
                <child.icon className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{child.title}</span>
                {child.badge && (
                  <Badge variant="outline" className="ml-auto text-xs px-2 py-0.5">
                    {child.badge}
                  </Badge>
                )}
              </NavLink>
            </SidebarMenuItem>
          ))}
        </div>
      )}
    </SidebarMenuItem>
  );
}

export function AppSidebar() {
  const { state } = useSidebar();
  const location = useLocation();
  const isMobile = useIsMobile();
  const collapsed = !isMobile && state === "collapsed";

  const currentPath = location.pathname;

  const [eventCount, setEventCount] = useState(0);

  // Get user role from Redux
  const { user, isAuthenticated, loading } = useAppSelector((state) => state.auth);
  const userRole = user?.role || null;

  if (loading) return null; // or a loader/spinner

  useEffect(() => {
    const events = localStorage.getItem("generatedEvents");
    if (events) setEventCount(JSON.parse(events).length);
  }, []);


  // Helper function to filter items based on user role
  const filterItemsByRole = (items: NavItem[]): NavItem[] => {
    return items.filter(item => {
      // If no roles specified, check if it's an auth page
      if (!item.allowedRoles || item.allowedRoles.length === 0) {
        if (item.url === '/signin' || item.url === '/signup') {
          return !isAuthenticated;
        }
        return true;
      }


      // If user is not authenticated, hide role-protected items
      if (!isAuthenticated || !userRole) {
        return false;
      }

      // Check if user's role is in the allowed roles
      return item.allowedRoles.includes(userRole);
    });
  };

  // Filter bottom links based on role
  const filteredBottomLinks = filterItemsByRole(bottomLinks);

  const getNavCls = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 rounded-lg px-2.5 py-2.5 text-sm font-medium min-w-0 transition-all duration-200 ${isActive
      ? "bg-gradient-to-r from-theme-primary to-theme-secondary text-white shadow-md"
      : "text-sidebar-foreground hover:text-foreground hover:bg-sidebar-accent/80"
    }`;

  return (
    <>
      {/* Enhanced custom scrollbar styles */}
      <style>{`
          .sidebar-scroll {
            scrollbar-width: none; /* Firefox */
            -ms-overflow-style: none; /* IE and Edge */
          }
          .sidebar-scroll::-webkit-scrollbar {
            display: none; /* Chrome, Safari, Opera */
          }
        `}</style>

      <Sidebar
        className={`flex flex-col border-r border-sidebar-border bg-sidebar transition-all duration-300 ease-in-out 
    ${collapsed ? "w-16" : "w-64"} sm:w-64`}
        collapsible="icon"
      >

        <SidebarContent className="h-full flex flex-col">
          {/* Enhanced Brand Section */}
          <div className={`flex items-center border-b border-sidebar-border bg-sidebar ${collapsed ? "justify-center px-2 py-3.5" : "gap-3 px-6 py-3"
            }`}>
            <Tooltip delayDuration={500} disableHoverableContent={!collapsed}>
              <TooltipTrigger asChild>
                <Zap className={`text-theme-primary flex-shrink-0 ${collapsed ? "w-7 h-7" : "w-8 h-8"
                  }`} />
              </TooltipTrigger>
              <TooltipContent side="right" className="whitespace-nowrap">
                Swaraah
              </TooltipContent>
            </Tooltip>
            {!collapsed && (
              <span className="text-xl font-bold gradient-text tracking-tight">
                Swaraah
              </span>
            )}
          </div>

          {/* Enhanced Scrollable Content */}
          <div className="flex-1 overflow-y-auto sidebar-scroll">
            {navigationSections.map((section, sectionIndex) => {
              // Filter items based on user role
              const filteredItems = filterItemsByRole(section.items);

              // Don't render empty sections
              if (filteredItems.length === 0) return null;

              return (
                <SidebarGroup key={section.label} className={sectionIndex > 0 ? "mt-1" : ""}>
                  <SidebarGroupLabel
                    className={
                      collapsed
                        ? "sr-only"
                        : "px-6 mb-2 text-xs uppercase text-sidebar-foreground/60 font-semibold tracking-wider"
                    }
                  >
                    {section.label}
                  </SidebarGroupLabel>
                  <SidebarGroupContent className={collapsed ? "px-0" : "px-3"}>
                    <SidebarMenu className="space-y-1">
                      {filteredItems.map((item) =>
                        item.children ? (
                          <SidebarSubMenu
                            key={item.title}
                            item={item}
                            collapsed={collapsed}
                            currentPath={currentPath}
                          />
                        ) : (
                          <SidebarMenuItem key={item.title}>
                            <NavLink to={item.url || "#"} className={getNavCls} end>
                              <Tooltip delayDuration={500} disableHoverableContent={!collapsed}>
                                <TooltipTrigger asChild>
                                  <item.icon className={`flex-shrink-0 ${collapsed ? "w-5 h-5" : "w-4 h-4"
                                    }`} />
                                </TooltipTrigger>
                                <TooltipContent side="right" className="whitespace-nowrap">
                                  {item.title}
                                </TooltipContent>
                              </Tooltip>

                              {!collapsed && (
                                <>
                                  <span className="truncate flex-1">{item.title}</span>
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    {item.badge && (
                                      <Badge
                                        variant="outline"
                                        className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-200"
                                      >
                                        {item.badge}
                                      </Badge>
                                    )}
                                    {item.title === "Events" && eventCount > 0 && (
                                      <Badge
                                        variant="secondary"
                                        className="text-xs px-2 py-0.5 bg-theme-primary text-white"
                                      >
                                        {eventCount}
                                      </Badge>
                                    )}
                                  </div>
                                </>
                              )}
                            </NavLink>
                          </SidebarMenuItem>
                        )
                      )}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
              );
            })}
          </div>

          {/* Enhanced Bottom Section */}
          {filteredBottomLinks.length > 0 && (
            <SidebarGroup className="mt-auto border-t border-sidebar-border bg-sidebar">
              <SidebarGroupContent className={`${collapsed ? "px-0" : "px-3"}`}>
                <SidebarMenu>
                  {filteredBottomLinks.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <NavLink to={item.url || "#"} className={getNavCls} end>
                        <Tooltip delayDuration={500} disableHoverableContent={!collapsed}>
                          <TooltipTrigger asChild>
                            <item.icon className={`flex-shrink-0 ${collapsed ? "w-5 h-5" : "w-4 h-4"
                              }`} />
                          </TooltipTrigger>
                          <TooltipContent side="right" className="whitespace-nowrap">
                            {item.title}
                          </TooltipContent>
                        </Tooltip>
                        {!collapsed && <span className="truncate">{item.title}</span>}
                      </NavLink>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>

              </SidebarGroupContent>
            </SidebarGroup>
          )}
        </SidebarContent>
      </Sidebar>
    </>
  );
}
